import type { FormControlLabelProps, RadioGroupProps } from '@mui/material'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { SimpleFieldError } from './SimpleFieldError'

interface SimpleRadioProps {
  label?: string
  options: Omit<FormControlLabelProps, 'control'>[]
  radioGroupProps: RadioGroupProps
  isOnDarkBg?: boolean
  labelBold?: boolean
  name?: string
  optionChildren?: React.ReactNode[]
}

const labelWrapperBase =
  'pt-[9px] ml:pt-[5px] mr-px-16 w-full text-sm md:text-base lg:text-base'
const labelTextBase =
  'ml-px-12 text-px-16 leading-px-24 ml:text-px-20 ml:leading-px-30'

export const SimpleRadio = React.forwardRef<HTMLInputElement, SimpleRadioProps>(
  (
    {
      name,
      label,
      radioGroupProps,
      optionChildren,
      options,
      isOnDarkBg = false,
      labelBold = false,
    },
    ref
  ) => {
    const formContext = useFormContext()
    const error = name
      ? formContext.getFieldState(name, formContext.formState).error?.message
      : undefined

    return (
      <FormControl
        component="fieldset"
        color="primary"
        style={isOnDarkBg ? { color: 'white' } : {}}
      >
        {label && <FormLabel component="legend">{label}</FormLabel>}
        <RadioGroup {...radioGroupProps}>
          {options.map((option, idx) => {
            const optionChild = optionChildren?.[idx]
            return (
              <React.Fragment key={option.value as any}>
                <FormControlLabel
                  inputRef={ref}
                  control={
                    <Radio
                      color="secondary"
                      style={isOnDarkBg ? { color: 'white' } : {}}
                    />
                  }
                  {...option}
                  label={
                    option.label ? (
                      <div className={labelWrapperBase}>
                        <p
                          style={{ fontWeight: labelBold ? 'bold' : 'normal' }}
                          className={labelTextBase}
                        >
                          {option.label}
                        </p>
                      </div>
                    ) : null
                  }
                />
                {optionChild ? (
                  <div style={{ marginLeft: '42px' }} className="mb-px-16">
                    {optionChild}
                  </div>
                ) : null}
              </React.Fragment>
            )
          })}
        </RadioGroup>
        {!!error && <SimpleFieldError>{error}</SimpleFieldError>}
      </FormControl>
    )
  }
)

SimpleRadio.displayName = 'SimpleRadio'
